import React, { useState, useEffect } from "react";
import MuiModal from "../MuiModal";
import { Button, TextField, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const FlexpertPaymentModal = ({ open, onClose, onSubmit, consultantName, isLoading = false, initialSalaryDetails = null }) => {
  const [rateType, setRateType] = useState("HOURLY");
  
  const [hourlyCurrency, setHourlyCurrency] = useState("INR");
  const [hourlyMinRate, setHourlyMinRate] = useState("");
  const [hourlyMaxRate, setHourlyMaxRate] = useState("");

  const [monthlyCurrency, setMonthlyCurrency] = useState("INR");
  const [monthlyMinRate, setMonthlyMinRate] = useState("");
  const [monthlyMaxRate, setMonthlyMaxRate] = useState("");

  const currentCurrency = rateType === "HOURLY" ? hourlyCurrency : monthlyCurrency;
  const currentMinRate = rateType === "HOURLY" ? hourlyMinRate : monthlyMinRate;
  const currentMaxRate = rateType === "HOURLY" ? hourlyMaxRate : monthlyMaxRate;

  const setCurrentCurrency = (value) => {
    if (rateType === "HOURLY") {
      setHourlyCurrency(value);
    } else {
      setMonthlyCurrency(value);
    }
  };

  const setCurrentMinRate = (value) => {
    if (rateType === "HOURLY") {
      setHourlyMinRate(value);
    } else {
      setMonthlyMinRate(value);
    }
  };

  const setCurrentMaxRate = (value) => {
    if (rateType === "HOURLY") {
      setHourlyMaxRate(value);
    } else {
      setMonthlyMaxRate(value);
    }
  };

  useEffect(() => {
    if (open && initialSalaryDetails && initialSalaryDetails.length > 0) {
      const hourlyDetail = initialSalaryDetails.find(detail => detail.type === "HOURLY");
      const monthlyDetail = initialSalaryDetails.find(detail => detail.type === "MONTHLY");

      if (hourlyDetail) {
        setHourlyCurrency(hourlyDetail.currency || "INR");
        setHourlyMinRate(hourlyDetail.min?.toString() || "");
        setHourlyMaxRate(hourlyDetail.max?.toString() || "");
      } else {
        setHourlyCurrency("INR");
        setHourlyMinRate("");
        setHourlyMaxRate("");
      }

      if (monthlyDetail) {
        setMonthlyCurrency(monthlyDetail.currency || "INR");
        setMonthlyMinRate(monthlyDetail.min?.toString() || "");
        setMonthlyMaxRate(monthlyDetail.max?.toString() || "");
      } else {
        setMonthlyCurrency("INR");
        setMonthlyMinRate("");
        setMonthlyMaxRate("");
      }

      if (hourlyDetail) {
        setRateType("HOURLY");
      } else if (monthlyDetail) {
        setRateType("MONTHLY");
      } else {
        setRateType("HOURLY");
      }
    } else if (open && !initialSalaryDetails) {
      setRateType("HOURLY");
      setHourlyCurrency("INR");
      setHourlyMinRate("");
      setHourlyMaxRate("");
      setMonthlyCurrency("INR");
      setMonthlyMinRate("");
      setMonthlyMaxRate("");
    }
  }, [open, initialSalaryDetails]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!hourlyMinRate && !monthlyMinRate) {
      return;
    }

    const salaryDetails = [];

    if (hourlyMinRate) {
      const hourlyMaxRateNum = hourlyMaxRate ? parseFloat(hourlyMaxRate) : null;
      salaryDetails.push({
        min: parseFloat(hourlyMinRate),
        ...(hourlyMaxRateNum && hourlyMaxRateNum > 0 && { max: hourlyMaxRateNum }),
        type: "HOURLY",
        currency: hourlyCurrency
      });
    }

    if (monthlyMinRate) {
      const monthlyMaxRateNum = monthlyMaxRate ? parseFloat(monthlyMaxRate) : null;
      salaryDetails.push({
        min: parseFloat(monthlyMinRate),
        ...(monthlyMaxRateNum && monthlyMaxRateNum > 0 && { max: monthlyMaxRateNum }),
        type: "MONTHLY",
        currency: monthlyCurrency
      });
    }

    onSubmit({
      flexExpert: true,
      salaryDetails: salaryDetails
    });
  };

  const handleClose = () => {
    setRateType("HOURLY");
    setHourlyCurrency("INR");
    setHourlyMinRate("");
    setHourlyMaxRate("");
    setMonthlyCurrency("INR");
    setMonthlyMinRate("");
    setMonthlyMaxRate("");
    onClose();
  };

  return (
    <MuiModal
      open={open}
      onCancel={handleClose}
      title={`Set Payment Details for ${consultantName || "Consultant"}`}
      maxWith="sm"
      styling={{ padding: "40px 32px" }}
    >
      <div style={{ paddingTop: "8px" }}>
        <form onSubmit={handleSubmit}>
        {/* Rate Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select Rate Type
          </label>
          <div className="flex gap-4">
            <div
              onClick={() => setRateType("HOURLY")}
              className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all relative ${
                rateType === "HOURLY"
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 bg-white hover:border-gray-400"
              }`}
            >
              <span className="absolute top-2 right-2 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                Recommended
              </span>
              <div className="flex items-center justify-center mb-2">
                <TrendingUpIcon
                  sx={{
                    fontSize: 32,
                    color: rateType === "HOURLY" ? "#3b82f6" : "#6b7280"
                  }}
                />
              </div>
              <div className="text-center font-medium text-gray-800">
                Hourly Rate
              </div>
            </div>
            <div
              onClick={() => setRateType("MONTHLY")}
              className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                rateType === "MONTHLY"
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 bg-white hover:border-gray-400"
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <CalendarTodayIcon
                  sx={{
                    fontSize: 32,
                    color: rateType === "MONTHLY" ? "#3b82f6" : "#6b7280"
                  }}
                />
              </div>
              <div className="text-center font-medium text-gray-800">
                Monthly Rate
              </div>
            </div>
          </div>
        </div>

        {/* Rate Details Input */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <FormControl fullWidth>
            <InputLabel id="currency-label">Currency *</InputLabel>
            <Select
              labelId="currency-label"
              value={currentCurrency}
              label="Currency *"
              onChange={(e) => setCurrentCurrency(e.target.value)}
              required
            >
              <MenuItem value="INR">₹ INR</MenuItem>
              <MenuItem value="USD">$ USD</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Min Rate"
            type="number"
            value={currentMinRate}
            onChange={(e) => setCurrentMinRate(e.target.value)}
            required
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label="Max Rate"
            type="number"
            value={currentMaxRate}
            onChange={(e) => setCurrentMaxRate(e.target.value)}
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || (!hourlyMinRate && !monthlyMinRate)}
            sx={{
              bgcolor: "#2EBAA3",
              "&:hover": { bgcolor: "#27a58f" },
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 500
            }}
          >
            {isLoading ? "Updating..." : "Update Status"}
          </Button>
        </div>
      </form>
      </div>
    </MuiModal>
  );
};

export default FlexpertPaymentModal;

